import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../common/axios";

/**
 * Knox SSO 팝업 창 컴포넌트 (LOA 결재 포탈)
 *
 * 네 가지 상태로 동작:
 * 1. 파라미터 없음 → SAML authn-request 조회 → IdP로 리다이렉트
 * 2. ?sso=complete       → opener에 SSO_SUCCESS 메시지 전송 후 닫기
 * 3. ?sso=not_registered → opener에 SSO_NO_USER 메시지(email 포함) 전송 후 닫기
 * 4. ?sso=error          → opener에 SSO_ERROR 메시지 전송 후 닫기
 */
export default function SsoPopup() {
  const [searchParams] = useSearchParams();
  const ssoParam = searchParams.get("sso");
  const reasonParam = searchParams.get("reason");

  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [errorMessage, setErrorMessage] = useState("");

  const targetOrigin = window.location.origin;

  useEffect(() => {
    if (ssoParam === "complete") {
      setStatus("success");
      if (window.opener) {
        window.opener.postMessage({ type: "SSO_SUCCESS" }, targetOrigin);
      }
      setTimeout(() => window.close(), 1500);
      return;
    }

    if (ssoParam === "not_registered") {
      setStatus("error");
      setErrorMessage("등록된 계정이 없습니다. 회원가입 페이지로 이동합니다.");
      (async () => {
        try {
          const res = await axios.get("/loasaml/temp-email", {
            withCredentials: true,
          });
          const email = res.data.email || "";
          if (window.opener) {
            window.opener.postMessage(
              { type: "SSO_NO_USER", email },
              targetOrigin,
            );
          }
        } catch (err) {
          if (window.opener) {
            window.opener.postMessage(
              { type: "SSO_NO_USER", email: "" },
              targetOrigin,
            );
          }
        }
        setTimeout(() => window.close(), 2000);
      })();
      return;
    }

    if (ssoParam === "error") {
      let msg = "Knox SSO 인증 중 오류가 발생했습니다.";
      if (reasonParam === "no_email") {
        msg = "이메일 정보를 가져올 수 없습니다.";
      } else if (reasonParam === "saml_unavailable") {
        msg = "Knox SSO 서비스를 현재 사용할 수 없습니다.";
      } else if (reasonParam === "validation_failed") {
        msg = "SAML 응답 검증에 실패했습니다.";
      }
      setStatus("error");
      setErrorMessage(msg);
      if (window.opener) {
        window.opener.postMessage(
          { type: "SSO_ERROR", error: msg },
          targetOrigin,
        );
      }
      setTimeout(() => window.close(), 2000);
      return;
    }

    // 파라미터 없음 → authn-request 조회 후 IdP로 이동
    (async () => {
      try {
        const res = await axios.get("/loasaml/authn-request", {
          withCredentials: true,
        });
        if (res.data.success && res.data.loginUrl) {
          window.location.href = res.data.loginUrl;
        } else {
          throw new Error("loginUrl을 받지 못했습니다.");
        }
      } catch (err) {
        const msg = "Knox SSO 인증 요청에 실패했습니다.";
        setStatus("error");
        setErrorMessage(msg);
        if (window.opener) {
          window.opener.postMessage(
            { type: "SSO_ERROR", error: msg },
            targetOrigin,
          );
        }
        setTimeout(() => window.close(), 2000);
      }
    })();
  }, [ssoParam, reasonParam, targetOrigin]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        fontFamily: "sans-serif",
        gap: 16,
      }}
    >
      {status === "loading" && (
        <>
          <div
            style={{
              width: 48,
              height: 48,
              border: "4px solid #e0e0e0",
              borderTopColor: "#1976d2",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: "#555", fontSize: 15 }}>Knox SSO 인증 중...</p>
        </>
      )}

      {status === "success" && (
        <>
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4caf50"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="9 12 11 14 15 10" />
          </svg>
          <p style={{ color: "#4caf50", fontSize: 15, fontWeight: 600 }}>
            로그인 성공! 창이 닫힙니다.
          </p>
        </>
      )}

      {status === "error" && (
        <>
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f44336"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <p style={{ color: "#f44336", fontSize: 15, fontWeight: 600 }}>
            {errorMessage || "오류가 발생했습니다."}
          </p>
          <p style={{ color: "#888", fontSize: 13 }}>창이 닫힙니다.</p>
        </>
      )}
    </div>
  );
}
